/**
 * @fileoverview TipTap `chartBlock` node — an atom block that stores a
 * ChartSpec (JSON in a data attribute) and renders it with recharts via a
 * React NodeView. Round-trips through the canvas `html` field so charts saved
 * to the canvas persist and re-render on load (and are captured by the
 * html2canvas PDF export).
 */
import { Node, mergeAttributes } from "@tiptap/core";
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from "@tiptap/react";
import type { ChartSpec } from "@cvx/lib/charts/spec";
import { encodeChartAttr, chartSpecFromAttr } from "./chartHtml";
import { ChartRenderer } from "../chartRenderer";

function ChartNodeView({ node }: ReactNodeViewProps) {
  const spec = node.attrs.spec as ChartSpec | null;
  return (
    <NodeViewWrapper className="my-3">
      {spec ? (
        <div className="rounded-lg border border-border bg-background p-3">
          <p className="mb-2 text-sm font-semibold">{spec.title}</p>
          <ChartRenderer spec={spec} />
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
          Unrenderable chart
        </div>
      )}
    </NodeViewWrapper>
  );
}

export const ChartNode = Node.create({
  name: "chartBlock",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      spec: {
        default: null,
        parseHTML: (el: HTMLElement) =>
          chartSpecFromAttr(el.getAttribute("data-morgan-chart") ?? ""),
        renderHTML: (attrs: { spec: ChartSpec | null }) =>
          attrs.spec
            ? { "data-morgan-chart": encodeChartAttr(attrs.spec) }
            : {},
      },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-morgan-chart]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ChartNodeView);
  },
});
